import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Calculator, 
  Calendar, 
  FileText, 
  Clock,
  Euro,
  TrendingUp,
  Download
} from 'lucide-react';
import { EmployeeList } from './EmployeeList';
import { PayrollCalculator } from './PayrollCalculator';
import { MonthlyReport } from './MonthlyReport';
import { PayrollHistory } from './PayrollHistory';
import { supabase } from '../../lib/supabaseClient';

export type TabType = 'employees' | 'calculator' | 'monthly' | 'history';

const Payroll: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('employees');
  const [stats, setStats] = useState({
    employeeCount: 0,
    totalHours: 0,
    totalSalaries: 0,
    isLoading: true
  });

  // Pobierz statystyki przy załadowaniu i gdy zmienia się zakładka
  useEffect(() => {
    loadStatistics();
  }, [activeTab]);

  const loadStatistics = async () => {
    try {
      // Pobierz liczbę pracowników
      const { data: employees, error: empError } = await supabase
        .from('employees')
        .select('id, base_salary, hourly_rate');

      if (empError) {
        console.error('Error loading employees:', empError);
        return;
      }

      const employeeCount = employees?.length || 0;

      // Pobierz dane z rozliczeń dla bieżącego miesiąca
      const currentMonth = new Date().toISOString().slice(0, 7);
      const { data: calculations, error: calcError } = await supabase
        .from('payroll_calculations')
        .select('regular_hours, overtime_50, overtime_100, overtime_regular, night_hours, weekend_hours, holiday_hours, gross_salary')
        .eq('month', currentMonth);

      let totalHours = 0;
      let totalSalaries = 0;

      if (calculations && calculations.length > 0) {
        // Sumuj godziny i wynagrodzenia z rzeczywistych rozliczeń
        calculations.forEach(calc => {
          totalHours += (calc.regular_hours || 0) + 
                       (calc.overtime_50 || 0) + 
                       (calc.overtime_100 || 0) + 
                       (calc.overtime_regular || 0) + 
                       (calc.night_hours || 0) + 
                       (calc.weekend_hours || 0) + 
                       (calc.holiday_hours || 0);
          totalSalaries += calc.gross_salary || 0;
        });
      } else if (employees && employees.length > 0) {
        // Jeśli nie ma rozliczeń, użyj szacunkowych danych
        // Zakładamy standardowe 168 godzin w miesiącu
        totalHours = employeeCount * 168;
        totalSalaries = employees.reduce((sum, emp) => {
          // Użyj base_salary jeśli jest, w przeciwnym razie oblicz z hourly_rate
          if (emp.base_salary) {
            return sum + emp.base_salary;
          } else if (emp.hourly_rate) {
            return sum + (emp.hourly_rate * 168);
          }
          return sum;
        }, 0);
      }

      setStats({
        employeeCount,
        totalHours: Math.round(totalHours),
        totalSalaries: Math.round(totalSalaries),
        isLoading: false
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
      setStats(prev => ({ ...prev, isLoading: false }));
    }
  };

  const tabs = [
    { id: 'employees' as TabType, label: 'Pracownicy', icon: Users },
    { id: 'calculator' as TabType, label: 'Kalkulator', icon: Calculator },
    { id: 'monthly' as TabType, label: 'Rozliczenie miesięczne', icon: Calendar },
    { id: 'history' as TabType, label: 'Historia', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Nagłówek */}
      <div className="bg-zinc-800 border-b border-zinc-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <Euro className="w-8 h-8 text-green-500" />
                  </div>
                  Rozliczenia Pracowników
                </h1>
                <p className="mt-1 text-sm text-gray-400">
                  Zarządzaj wynagrodzeniami i nadgodzinami
                </p>
              </div>
              
              {/* Statystyki */}
              <div className="hidden md:flex gap-3 lg:gap-4">
                <div className="bg-zinc-700 rounded-lg p-3 lg:p-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-400" />
                    <span className="text-xs lg:text-sm text-gray-400">Pracownicy</span>
                  </div>
                  <p className="text-xl lg:text-2xl font-bold text-white mt-1">
                    {stats.isLoading ? (
                      <span className="text-sm text-gray-500">...</span>
                    ) : (
                      stats.employeeCount
                    )}
                  </p>
                </div>
                <div className="bg-zinc-700 rounded-lg p-3 lg:p-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <span className="text-xs lg:text-sm text-gray-400">Godziny w tym miesiącu</span>
                  </div>
                  <p className="text-xl lg:text-2xl font-bold text-white mt-1">
                    {stats.isLoading ? (
                      <span className="text-sm text-gray-500">...</span>
                    ) : (
                      stats.totalHours.toLocaleString('pl-PL')
                    )}
                  </p>
                </div>
                <div className="bg-zinc-700 rounded-lg p-3 lg:p-4">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-gray-400" />
                    <span className="text-xs lg:text-sm text-gray-400">Suma wynagrodzeń</span>
                  </div>
                  <p className="text-xl lg:text-2xl font-bold text-green-400 mt-1">
                    {stats.isLoading ? (
                      <span className="text-sm text-gray-500">...</span>
                    ) : (
                      `${stats.totalSalaries.toLocaleString('pl-PL')} zł`
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Zakładki */}
          <div className="flex space-x-2 sm:space-x-4 lg:space-x-8 border-b border-zinc-700 -mb-px overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm transition-all flex items-center gap-1 sm:gap-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Zawartość */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {activeTab === 'employees' && <EmployeeList />}
        {activeTab === 'calculator' && <PayrollCalculator onSaveSuccess={loadStatistics} />}
        {activeTab === 'monthly' && <MonthlyReport />}
        {activeTab === 'history' && <PayrollHistory />}
      </div>
    </div>
  );
};

export default Payroll;
